import styled from "styled-components";
import {
  Callout,
  DirectionalHint,
  DefaultButton,
  PrimaryButton,
} from "@fluentui/react";
import React, { useContext, useMemo } from "react";
import { BooleanFilter } from "./BooleanFilter";
import { ColumnOptionsContext } from "./ColumnOptions";
import { DateFilter } from "./DateFilter";
import { NumberFilter } from "./NumberFilter";
import { TaxonomyFilter } from "./TaxonomyFilter";
import { TaxonomySelectFilter } from "./TaxonomySelectFilter";
import { TextFilter } from "./TextFilter";
import { Column } from "./types";
import { clearFilter } from "./utils";

type Props = {
  isOpen: boolean;
  close: () => void;
  target: React.RefObject<HTMLDivElement>;
  width: number;
  column: Column;
};

export const FilterDropdown = ({
  isOpen,
  close,
  target,
  width,
  column,
}: Props) => {
  const { setFilter, strings } = useContext(ColumnOptionsContext);

  const disableClearButton = useMemo(() => {
    if (!column.filter) return true;
    const f = column.filter;
    switch (f.type) {
      case "boolean":
        return f.value === null;
      case "text":
        return f.value === "";
      case "date":
        return f.date1 === null;
      case "number":
        return f.number1 === null;
      case "taxonomy":
        return !f.value.length;
      default:
        return true;
    }
  }, [column.filter]);

  if (!column.filter || !isOpen) return null;

  return (
    <Callout
      target={target.current}
      onDismiss={close}
      directionalHint={DirectionalHint.bottomLeftEdge}
      isBeakVisible
      setInitialFocus={false}
      styles={{
        root: { minWidth: Math.max(width, 250) },
        calloutMain: { minWidth: Math.max(width, 250) },
      }}
    >
      <FilterContent>
        {column.filter.type === "taxonomy" && !column.filter.isSelect && (
          <TaxonomyFilter column={column} />
        )}
        {column.filter.type === "taxonomy" && column.filter.isSelect && (
          <TaxonomySelectFilter column={column} />
        )}
        {column.filter.type === "date" && <DateFilter column={column} />}
        {column.filter.type === "number" && <NumberFilter column={column} />}
        {column.filter.type === "boolean" && <BooleanFilter column={column} />}
        {column.filter.type === "text" && <TextFilter column={column} />}
      </FilterContent>
      <FilterActions>
        <DefaultButton
          iconProps={{ iconName: "Cancel" }}
          disabled={disableClearButton}
          onClick={() => {
            if (!column.filter) return;
            const cleared = clearFilter(column.filter);
            if (cleared) setFilter(column.key, cleared);
          }}
        >
          {strings.clearFilterButtonLabel}
        </DefaultButton>
        <PrimaryButton
          iconProps={{ iconName: "Accept" }}
          onClick={close}
        >
          {strings.applyButtonLabel}
        </PrimaryButton>
      </FilterActions>
    </Callout>
  );
};

const FilterContent = styled.div`
  padding: 12px;
  overflow-y: auto;
  flex: 1;
  max-height: 300px;
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid ${(props) => props.theme.palette.neutralLight};
`;
